import React from 'react';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import {
  emptyPurchaseLinkDraft,
  purchaseLinkLabel,
  type PurchaseLinkDraft,
} from '../../utils/purchasingLinks';

const PurchaseLinksEditor: React.FC<{
  drafts: PurchaseLinkDraft[];
  onChange: (drafts: PurchaseLinkDraft[]) => void;
  readOnly?: boolean;
}> = ({ drafts, onChange, readOnly }) => {
  if (readOnly && drafts.length === 0) return null;

  const update = (index: number, patch: Partial<PurchaseLinkDraft>) => {
    onChange(drafts.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <div className="po-lines po-links">
      <div className="po-lines-head">
        <span>Additional links{readOnly ? '' : ' (optional)'}</span>
        {!readOnly && (
          <button
            type="button"
            className="fin-secondary-btn"
            onClick={() => onChange([...drafts, emptyPurchaseLinkDraft()])}
          >
            <Plus size={13} /> Add link
          </button>
        )}
      </div>
      {drafts.length === 0 ? (
        <p className="po-links-hint">A quote, cart, or spec that is not already linked on a line.</p>
      ) : readOnly ? (
        <ul className="po-links-list">
          {drafts.map(row => (
            <li key={row.key}>
              <a href={row.url} target="_blank" rel="noreferrer">
                <ExternalLink size={13} aria-hidden />
                {purchaseLinkLabel(row)}
              </a>
              {row.label ? (
                <a className="po-links-url" href={row.url} target="_blank" rel="noreferrer">
                  {row.url}
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="po-lines-table-wrap">
          <table className="po-lines-table">
            <colgroup>
              <col className="po-col-link-label" />
              <col className="po-col-link-url" />
              <col className="po-col-remove" />
            </colgroup>
            <thead>
              <tr>
                <th className="po-col-link-label">Label</th>
                <th className="po-col-link-url">URL</th>
                <th className="po-col-remove" />
              </tr>
            </thead>
            <tbody>
              {drafts.map((row, index) => (
                <tr key={row.key}>
                  <td className="po-col-link-label">
                    <input
                      className="fin-modal-input"
                      value={row.label}
                      onChange={e => update(index, { label: e.target.value })}
                      placeholder="Quote, cart…"
                    />
                  </td>
                  <td className="po-col-link-url">
                    <input
                      className="fin-modal-input"
                      type="url"
                      value={row.url}
                      onChange={e => update(index, { url: e.target.value })}
                      placeholder="https://"
                    />
                  </td>
                  <td className="po-col-remove">
                    <button
                      type="button"
                      className="po-lines-remove"
                      aria-label="Remove link"
                      onClick={() => onChange(drafts.filter((_, i) => i !== index))}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PurchaseLinksEditor;
